import "@/App.css";
import { 
  Phone, 
  MessageCircle, 
  Menu, 
  X, 
  Hammer, 
  Home, 
  Fence, 
  TreeDeciduous, 
  Wrench, 
  LayoutGrid,
  CheckCircle,
  MapPin,
  Star,
  Quote
} from "lucide-react";
import { useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const PHONE_NUMBER = "0409 138 341";
const PHONE_LINK = "tel:+61409138341";
const WHATSAPP_LINK = "https://wa.me/61409138341?text=Hi,%20I'd%20like%20a%20quote";

const Navigation = () => {
  const navLinks = [
    { href: "#services", label: "Services" },
    { href: "#about", label: "About" },
    { href: "#why-us", label: "Why Us" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <nav className="nav-sticky" data-testid="navigation">
      <div className="container">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="#" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-10 h-10 bg-[#EA580C] rounded-lg flex items-center justify-center">
              <Hammer className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-[#111827]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A&W Projects
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-[#4B5563] hover:text-[#EA580C] font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            ))}
            <a href={PHONE_LINK} className="btn-primary text-sm py-2 px-4" data-testid="nav-call-btn">
              <Phone className="w-4 h-4" />
              Call Now
            </a>
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <button className="p-2" data-testid="mobile-menu-trigger">
                  <Menu className="w-6 h-6 text-[#111827]" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-white">
                <VisuallyHidden>
                  <SheetTitle>Navigation Menu</SheetTitle>
                  <SheetDescription>Main navigation links for A&W Projects Carpentry</SheetDescription>
                </VisuallyHidden>
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map((link) => (
                    <SheetClose asChild key={link.href}>
                      <a
                        href={link.href}
                        className="text-lg text-[#111827] hover:text-[#EA580C] font-medium"
                        data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.label}
                      </a>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <a href={PHONE_LINK} className="btn-primary mt-4" data-testid="mobile-call-btn">
                      <Phone className="w-5 h-5" />
                      Call Now
                    </a>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

const HeroSection = () => {
  return (
    <section className="hero-section" data-testid="hero-section">
      <img
        src="https://customer-assets.emergentagent.com/job_carpentry-melb/artifacts/o2lz5pjg_2149290155.jpg"
        alt="Timber deck and wood cladding"
        className="hero-bg-image"
      />
      <div className="hero-overlay"></div>
      
      <div className="container relative z-10 pt-24 md:pt-0">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-5 h-5 text-[#EA580C]" />
            <span className="text-[#4B5563] font-medium">Melbourne, Australia</span>
          </div>
          
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#111827] tracking-tight mb-6"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="hero-headline"
          >
            Reliable Carpentry Services in Melbourne
          </h1>
          
          <p className="text-lg md:text-xl text-[#4B5563] mb-8 leading-relaxed" data-testid="hero-subheadline">
            Framing, decks, pergolas, renovations & cabinetry. Quality workmanship you can trust.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={PHONE_LINK} className="btn-primary text-lg" data-testid="hero-call-btn">
              <Phone className="w-5 h-5" />
              Call Now
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp text-lg" data-testid="hero-whatsapp-btn">
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const services = [
  {
    icon: Hammer,
    title: "Framing",
    description: "Structural timber framing for new builds and extensions. Built to Australian standards.",
  },
  {
    icon: Fence,
    title: "Decks",
    description: "Custom timber decks designed for Melbourne's climate. Built to last and look great.",
  },
  {
    icon: TreeDeciduous,
    title: "Pergolas",
    description: "Stylish pergolas that extend your living space outdoors. Perfect for entertaining.",
  },
  {
    icon: Home,
    title: "Renovations",
    description: "Home renovations done right. From minor updates to major transformations.",
  },
  {
    icon: LayoutGrid,
    title: "Cabinetry",
    description: "Custom cabinetry and built-in storage solutions. Crafted to your specifications.",
  },
];

const ServicesSection = () => {
  return (
    <section id="services" className="section" data-testid="services-section">
      <div className="container">
        <div className="text-center mb-12">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#111827] mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="services-heading"
          >
            Our Services
          </h2>
          <p className="text-[#4B5563] text-lg max-w-2xl mx-auto">
            Professional carpentry services for residential and commercial projects across Melbourne.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {services.map((service, index) => (
            <div key={index} className="service-card" data-testid={`service-card-${service.title.toLowerCase()}`}>
              <div className="service-card-icon">
                <service.icon />
              </div>
              <h3 
                className="text-xl font-bold text-[#111827] mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {service.title}
              </h3>
              <p className="text-[#4B5563] leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="section section-alt" data-testid="about-section">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1705760058049-b58119c416a9?auto=format&fit=crop&w=800&q=80"
              alt="Carpenter working with wood"
              className="about-image"
              data-testid="about-image"
            />
          </div>
          
          <div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#111827] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="about-heading"
            >
              About A&W Projects Carpentry
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed mb-6" data-testid="about-text">
              A&W Projects Carpentry is a Melbourne-based carpentry business delivering quality workmanship, clear communication and reliable service. From structural framing to custom cabinetry, every project is built to last.
            </p>
            <p className="text-[#4B5563] text-lg leading-relaxed mb-8">
              Led by Andrew Williams, we take pride in honest pricing, attention to detail, and completing jobs on time. Whether it's a backyard deck or a full renovation, we treat every project like it's our own.
            </p>
            
            <a href="#contact" className="btn-secondary" data-testid="about-contact-btn">
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const features = [
  "Melbourne local carpenter",
  "Quality workmanship guaranteed",
  "Reliable and professional service",
  "Residential & small commercial projects",
  "Clear pricing and communication",
  "Fully licensed and insured",
];

const WhyUsSection = () => {
  return (
    <section id="why-us" className="section" data-testid="why-us-section">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#111827] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="why-us-heading"
            >
              Why Choose Us
            </h2>
            <p className="text-[#4B5563] text-lg leading-relaxed mb-8">
              When you work with A&W Projects, you're getting a carpenter who cares about doing the job right. No shortcuts, no surprises.
            </p>
            
            <div className="space-y-4" data-testid="features-list">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <CheckCircle className="feature-icon" />
                  <span className="text-[#111827] font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div>
            <img
              src="https://images.unsplash.com/photo-1666936492606-ac8f7fdd8c15?auto=format&fit=crop&w=800&q=80"
              alt="Timber deck construction"
              className="about-image"
              data-testid="why-us-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const testimonials = [
  {
    name: "Michael Thompson",
    location: "Brunswick, Melbourne",
    rating: 5,
    text: "Andrew built our back deck and it's fantastic. He was upfront about costs, showed up when he said he would, and the finish is top quality. Highly recommend A&W Projects.",
  },
  {
    name: "Sarah Mitchell",
    location: "Preston, Melbourne",
    rating: 5,
    text: "We had A&W do our kitchen cabinetry. Andrew's attention to detail is impressive. Everything fits perfectly and the whole process was smooth from quote to completion.",
  },
  {
    name: "David Chen",
    location: "Northcote, Melbourne",
    rating: 5,
    text: "Professional, reliable, and excellent work. Our pergola looks amazing and was finished on time. Andrew communicated well throughout the project. Would definitely use again.",
  },
];

const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="section section-alt" data-testid="testimonials-section">
      <div className="container">
        <div className="text-center mb-12">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#111827] mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="testimonials-heading"
          >
            What Our Clients Say
          </h2>
          <p className="text-[#4B5563] text-lg max-w-2xl mx-auto">
            Don't just take our word for it. Here's what Melbourne homeowners are saying.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card" data-testid={`testimonial-card-${index}`}>
              <div className="flex items-center gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-[#EA580C] text-[#EA580C]" />
                ))}
              </div>
              <Quote className="w-8 h-8 text-[#E5E7EB] mb-3" />
              <p className="text-[#4B5563] leading-relaxed mb-6">
                "{testimonial.text}"
              </p>
              <div>
                <p className="font-bold text-[#111827]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {testimonial.name}
                </p>
                <p className="text-sm text-[#9CA3AF]">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  return (
    <section id="contact" className="section" data-testid="contact-section">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#111827] mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="contact-heading"
          >
            Get in Touch
          </h2>
          <p className="text-[#4B5563] text-lg mb-8">
            Ready to start your project? Call or message us on WhatsApp for a quick quote.
          </p>
          
          <a href={PHONE_LINK} className="contact-phone block mb-8 hover:text-[#EA580C] transition-colors duration-200" data-testid="contact-phone">
            {PHONE_NUMBER}
          </a>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK} className="btn-primary text-lg" data-testid="contact-call-btn">
              <Phone className="w-5 h-5" />
              Call Now
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp text-lg" data-testid="contact-whatsapp-btn">
              <MessageCircle className="w-5 h-5" />
              WhatsApp Us
            </a>
          </div>
          
          <div className="mt-12 p-6 bg-[#F9FAFB] rounded-xl">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-[#EA580C]" />
              <span className="font-medium text-[#111827]">Servicing Melbourne & Surrounding Areas</span>
            </div>
            <p className="text-[#4B5563]">
              Brunswick, Preston, Northcote, Fitzroy, Carlton, Collingwood, Richmond, and more.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#EA580C] rounded-lg flex items-center justify-center">
              <Hammer className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              A&W Projects Carpentry
            </span>
          </div>
          
          <p className="text-[#9CA3AF] text-sm text-center md:text-right">
            © {currentYear} A&W Projects Carpentry. Melbourne, Australia.
          </p>
        </div>
      </div>
    </footer>
  );
};

const MobileStickyBar = () => {
  return (
    <div className="mobile-sticky-bar" data-testid="mobile-sticky-bar">
      <a href={PHONE_LINK} className="btn-primary" data-testid="mobile-bar-call-btn">
        <Phone className="w-5 h-5" />
        Call Now
      </a>
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-whatsapp" data-testid="mobile-bar-whatsapp-btn">
        <MessageCircle className="w-5 h-5" />
        WhatsApp
      </a>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Navigation />
      <main className="main-content">
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <WhyUsSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}

export default App;
